import { useState, type FormEvent } from 'react';
import { useCreateMtoApplicationMutation } from '@shared/api';
import type { MaterialLot, NewLotApplication } from '@entities/mto';
import { apiErrorMessage } from '@entities/mto';
import { Button } from '@shared/ui/Button';
import { Input, Label, Select } from '@shared/ui/Input';
import { lotLabel } from '../helpers';
import {
  AddAppCard,
  Form,
  FormActions,
  FormField,
  InlineHint,
  SectionTitle,
} from '../styles';

export function AddApplicationForm({
  lots,
  canWrite,
  onError,
}: {
  lots: MaterialLot[];
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [lotId, setLotId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [elementId, setElementId] = useState('');
  const [factRef, setFactRef] = useState('');
  const [appliedDate, setAppliedDate] = useState('');
  const [createApp, { isLoading }] = useCreateMtoApplicationMutation();

  function reset() {
    setLotId('');
    setSiteId('');
    setElementId('');
    setFactRef('');
    setAppliedDate('');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onError('');
    if (!lotId) {
      onError('Выберите партию.');
      return;
    }
    const body: NewLotApplication = { lot_id: lotId };
    if (siteId.trim()) body.site_id = siteId.trim();
    if (elementId.trim()) body.element_id = elementId.trim();
    if (factRef.trim()) body.applied_fact_ref = factRef.trim();
    if (appliedDate.trim()) body.applied_date = appliedDate.trim();
    try {
      await createApp(body).unwrap();
      reset();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось добавить применение.');
      // eslint-disable-next-line no-console
      console.error('createMtoApplication failed', err);
    }
  }

  return (
    <AddAppCard>
      <SectionTitle>Зафиксировать применение партии</SectionTitle>
      <Form onSubmit={handleSubmit}>
        <FormField>
          <Label htmlFor="qc-app-lot">Партия *</Label>
          <Select id="qc-app-lot" value={lotId} onChange={(e) => setLotId(e.target.value)} aria-required="true">
            <option value="">— выберите партию —</option>
            {lots.map((lot) => (
              <option key={lot.id} value={lot.id}>
                {lotLabel(lot)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField>
          <Label htmlFor="qc-app-site">Объект</Label>
          <Input id="qc-app-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} placeholder="Объект, например ДГП-2" />
        </FormField>
        <FormField>
          <Label htmlFor="qc-app-element">Элемент</Label>
          <Input
            id="qc-app-element"
            value={elementId}
            onChange={(e) => setElementId(e.target.value)}
            placeholder="Конструктив / ось"
          />
        </FormField>
        <FormField>
          <Label htmlFor="qc-app-ref">Ссылка на факт</Label>
          <Input
            id="qc-app-ref"
            value={factRef}
            onChange={(e) => setFactRef(e.target.value)}
            placeholder="АОСР / журнал работ"
          />
        </FormField>
        <FormField>
          <Label htmlFor="qc-app-date">Дата применения</Label>
          <Input id="qc-app-date" type="date" value={appliedDate} onChange={(e) => setAppliedDate(e.target.value)} />
        </FormField>
        <FormActions>
          <Button type="submit" $variant="primary" $size="sm" disabled={!canWrite || isLoading || lots.length === 0}>
            {isLoading ? 'Добавляю…' : 'Зафиксировать'}
          </Button>
          {lots.length === 0 && <InlineHint>Сначала добавьте партию в реестре.</InlineHint>}
        </FormActions>
      </Form>
    </AddAppCard>
  );
}
